<template>
  <div
    class="locale-changer"
    :class="{
      'locale-changer--compact': compact,
      'locale-changer--nav': navRow,
    }"
  >
    <button
      ref="trigger"
      href="#"
      class="DropdownList-selectLanguage"
      @click="toggleVisibility"
      @keydown.space.exact.prevent="toggleVisibility"
      @keydown.esc.exact="closeAndRestoreFocus"
      @keydown.shift.tab="hideDropdown"
      @keydown.up.exact.prevent="startArrowKeys"
      @keydown.down.exact.prevent="startArrowKeys"
    >
      <!--
        WP-04V-C (D3): as a sidebar nav row the trigger reads like every other
        row -- canonical globe glyph, the «تغییر زبان» label, and the current
        locale as a trailing badge -- instead of flag + code + caret. The
        dropdown below and every method backing it are untouched.
      -->
      <template v-if="navRow">
        <student-home-icon name="language" :size="22" class="icon" />
        <span class="locale-changer__label">{{
          $t('appSidebar.sidebarLinks.changeLanguage')
        }}</span>
        <em class="locale-changer__badge">{{ $i18n.locale.toUpperCase() }}</em>
      </template>
      <template v-else>
        <img :src="getLocalLanguageImg()" alt="flag" width="24" />
        <span class="mx-2">{{ $i18n.locale.toUpperCase() }}</span>
        <img
          src="@/assets/arrow-down.png"
          alt="down arrow"
          color="c-soft-black"
          width="10"
        />
      </template>
    </button>
    <transition name="dropdown-fade">
      <ul
        v-if="isVisible"
        ref="dropdown"
        class="absolute normal-case font-normal xs:left-0 lg:right-0 bg-white
           shadow overflow-hidden rounded w-48 border mt-0 py-1 lg-z:20"
      >
        <li>
          <a
            href="#"
            @click.prevent="setLocale('en')"
            ref="english-flag"
            class="flex items-center px-3 hover:bg-gray-200"
            @keydown.esc.exact="closeAndRestoreFocus"
            @keydown.tab.exact="focusNext(false)"
            @keydown.up.exact.prevent=""
            @keydown.down.exact.prevent="focusNext(true)"
          >
            <img src="@/assets/flag-en.png" alt="flag" width="24" />
            <span class="ml-2">English</span>
          </a>
        </li>
        <li>
          <a
            href="#"
            @click.prevent="setLocale('fa')"
            class="flex items-center px-3 hover:bg-gray-200"
            @keydown.esc.exact="closeAndRestoreFocus"
            @keydown.tab.exact="hideDropdown"
            @keydown.up.exact.prevent="focusPrevious(true)"
            @keydown.down.exact.prevent=""
            @keydown.shift.tab="focusPrevious(false)"
          >
            <img src="@/assets/flag-fa.png" alt="flag" width="24" />
            <span class="ml-2">Farsi</span>
          </a>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'

export default {
  components: { StudentHomeIcon },
  props: {
    // P3 full-shell visual remediation (student-foundation-redesign, 00_*.md
    // §13): a small header-utility rendering of the SAME real language
    // switcher, matching the rendered prototype's utility-button geometry
    // (00_*.md §13.1). Behavior (setLocale/dropdown) is completely
    // unchanged; only the trigger button's size/shape differs. Default
    // (false) renders byte-identically to before, for every existing
    // sidebar usage.
    compact: {
      type: Boolean,
      default: false,
    },
    // WP-04V-C (D3): renders the SAME switcher as a student sidebar nav row.
    // Presentation only -- `setLocale` and the dropdown are unchanged.
    navRow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isVisible: false,
      focusedIndex: 0,
    }
  },
  // P39 TR-020 (D-B1-5): the sidebar stays mounted across routes, so the open
  // list has to be closed on its own -- by any navigation and by a click
  // anywhere outside the switcher. (The `v-on-clickaway` directive this used
  // was never registered, and the `@keyDown` listeners never fired: browsers
  // dispatch `keydown`.)
  watch: {
    $route() {
      this.hideDropdown()
    },
    isVisible(open) {
      if (open) {
        document.addEventListener('click', this.onDocumentClick, true)
      } else {
        document.removeEventListener('click', this.onDocumentClick, true)
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick, true)
  },
  methods: {
    onDocumentClick(event) {
      if (this.$el && !this.$el.contains(event.target)) this.hideDropdown()
    },
    // Escape closes the list and gives focus back to the trigger, so keyboard
    // users stay where they opened it.
    closeAndRestoreFocus() {
      this.hideDropdown()
      const trigger = this.$refs.trigger
      if (trigger && typeof trigger.focus === 'function') trigger.focus()
    },
    toggleVisibility() {
      this.isVisible = !this.isVisible
    },
    hideDropdown() {
      this.isVisible = false
      this.focusedIndex = 0
    },
    startArrowKeys() {
      if (this.isVisible) {
        this.$refs.dropdown.children[0].focus()
      }
    },
    focusPrevious(isArrowKey) {
      this.focusedIndex = this.focusedIndex - 1
      if (isArrowKey) {
        this.focusItem()
      }
    },
    focusNext(isArrowKey) {
      this.focusedIndex = this.focusedIndex + 1
      if (isArrowKey) {
        this.focusItem()
      }
    },
    focusItem() {
      this.$refs.dropdown.children[this.focusedIndex].children[0].focus()
    },
    setLocale(locale) {
      // P40 N-3: re-picking the active language only closes the list. A push
      // to the current route would reject with NavigationDuplicated.
      if (locale === this.$i18n.locale) {
        this.hideDropdown()
        return
      }

      this.$i18n.locale = locale

      this.$router
        .push({
          params: { lang: locale },
        })
        .catch(() => {})

      this.hideDropdown()
    },
    getLocalLanguageImg() {
      return require(`@/assets/flag-${this.$i18n.locale}.png`)
    },
  },
}
</script>

<style lang="scss" scoped>
.locale-changer {
  position: relative;
}

.locale-changer ul {
  position: absolute;
  width: 100%;
  list-style-type: none;
  padding-left: 0;
}

.locale-changer ul li {
  margin-bottom: 10px;
  margin-top: 10px;
}

.locale-changer ul li a {
  text-decoration: none;
}

.DropdownList-selectLanguage {
  @include font-category('body');

  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid $c-border-grey;
  border-radius: 4px;
  width: 100%;
  padding: 10px 16px;

  &:disabled {
    color: $c-disabled-grey;
  }
}

// P3 full-shell visual remediation (00_*.md §13): compact header-utility
// trigger, matching the rendered prototype's icon-button sizing
// (00_*.md §13.1, `.icon-button`) -- same real dropdown/behavior below it.
.locale-changer--compact {
  .DropdownList-selectLanguage {
    width: auto;
    height: 42px;
    padding: 0 12px;
    border-radius: 14px;
    border-color: #e2e8f0;
  }

  .locale-changer ul,
  ul {
    width: max-content;
    min-width: 140px;
  }
}

// WP-04V-C (D3): the student sidebar nav-row rendering. Every value measured
// from the rendered prototype's `.nav-item` language row -- 48px tall, 14px
// radius, 22px glyph, 11px gap, 0 14px padding, 12px/700 label, and an 8px/700
// locale badge pushed to the row's far end.
.locale-changer--nav {
  .DropdownList-selectLanguage {
    background: transparent;
    border: 0;
    border-radius: $borna-radius-nav;
    color: $borna-muted;
    gap: 11px;
    height: $borna-nav-row-height;
    justify-content: flex-start;
    padding: 0 14px;
    width: 100%;
  }

  .icon {
    flex: 0 0 auto;
  }

  .locale-changer__label {
    font-size: $borna-fs-nav;
    font-weight: $borna-fw-bold;
  }

  .locale-changer__badge {
    background: $borna-canvas;
    border-radius: 7px;
    font-size: 8px;
    font-style: normal;
    font-weight: $borna-fw-bold;
    margin-inline-start: auto;
    padding: 3px 6px;
  }

  ul {
    width: max-content;
    min-width: 140px;
  }
}
</style>
